import { Injectable } from '@angular/core';
import { Observable, map } from 'rxjs';
import { EntityKeyByReference, GlobalEntityDefinition } from '@card-software/library';
import { TranslateService } from '@ngx-translate/core';
import { BaseTransactionStrategy } from './base-transaction.strategy';
import { SupplyWarehouseDocumentType } from '../../models/supply-warehouse/supply-warehouse-document-type.enum';
import { TableColumn } from '../../models/supply-warehouse/table-column.model';
import { TransactionAction } from '../../models/supply-warehouse/transaction-action.model';
import { InternalReturn, InternalReturnStatus } from '../../models/inventory/internal-return';
import { DispatchRequest } from '../../models/dispatch-request/dispatch-request';
import { InternalReturnProxyService } from '../../proxies/inventory/internal-return.proxy.service';
import { DispatchRequestProxyService } from '../../proxies/inventory/dispatch-request.proxy.service';

/**
 * Estrategia para manejar Internal Returns (IR)
 * Documenta la devolución de materiales al inventario desde Dispatch Requests completados
 */
@Injectable({
  providedIn: 'root',
})
export class InternalReturnStrategy extends BaseTransactionStrategy<InternalReturn, InternalReturnStatus> {
  readonly documentType = SupplyWarehouseDocumentType.InternalReturn;
  readonly initiatorName = 'Dispatch Request';
  readonly locationAssignmentType = 'storage' as const;

  constructor(
    private internalReturnService: InternalReturnProxyService,
    private dispatchRequestService: DispatchRequestProxyService,
    private translateService: TranslateService,
  ) {
    super();
  }

  private translate(key: string, fallback: string): string {
    const result = this.translateService.instant(key);
    return result === key ? fallback : result;
  }

  loadDocument(id: string): Observable<InternalReturn | null> {
    return this.internalReturnService.getById(id).pipe(map((response) => response.data || null));
  }

  loadInitiatorDocument(dispatchRequestId: string): Observable<DispatchRequest | null> {
    return this.dispatchRequestService.getById(dispatchRequestId).pipe(map((response) => response.data || null));
  }

  searchTransactionDocuments(dispatchRequestId: string): Observable<InternalReturn[]> {
    const entityKey = this.getInitiatorEntityKey(dispatchRequestId);
    return this.internalReturnService.search({ initiator: entityKey }).pipe(map((response) => response.data?.items || []));
  }

  createFromInitiator(dispatchRequestId: string): Observable<InternalReturn> {
    const entityKey = this.getInitiatorEntityKey(dispatchRequestId);
    return this.internalReturnService.createFromEntityKey(entityKey).pipe(map((response) => response.data!));
  }

  updateDocument(id: string, document: InternalReturn): Observable<InternalReturn> {
    return this.internalReturnService.update(id, document).pipe(map((response) => response.data!));
  }

  updateStatus(id: string, newStatus: InternalReturnStatus): Observable<InternalReturn> {
    return this.internalReturnService.updateStatus(id, newStatus).pipe(map((response) => response.data!));
  }

  getTableColumns(): TableColumn[] {
    return [
      { field: 'itemCode', headerKey: 'IR_Item_Code', header: 'Item Code', width: '150px', type: 'text', sortable: true },
      { field: 'itemDescription', headerKey: 'IR_Item_Description', header: 'Item Description', width: '250px', type: 'text' },
      { field: 'quantity', headerKey: 'IR_Quantity', header: 'IR Quantity', width: '120px', type: 'numeric', editable: true },
      { field: 'uom', headerKey: 'IR_UOM', header: 'UOM', width: '100px', type: 'text' },
      { field: 'storageLocations', headerKey: 'GRN_Storage_Locations', header: 'Storage Locations', type: 'locations', editable: true },
      { field: 'comments', headerKey: 'IR_Comments', header: 'IR Comments', width: '200px', type: 'text', editable: true },
    ];
  }

  getInitiatorItemsColumns(): TableColumn[] {
    return [
      { field: 'itemCode', headerKey: 'IR_Item_Code', header: 'Item Code', width: '150px', sortable: true },
      { field: 'itemDisplayName', headerKey: 'IR_Item_Description', header: 'Item Description', width: '250px' },
      { field: 'quantity', headerKey: 'IR_Dispatched_Qty', header: 'Dispatched Qty', width: '120px', type: 'numeric' },
      { field: 'uom', headerKey: 'IR_UOM', header: 'UOM', width: '100px', type: 'text' },
      { field: 'returnedQty', headerKey: 'IR_Returned_Qty', header: 'Returned Qty', width: '120px', type: 'numeric' },
      { field: 'remaining', headerKey: 'IR_Remaining', header: 'Remaining', width: '120px', type: 'numeric' },
      { field: 'actions', header: '', width: '80px', type: 'actions' },
    ];
  }

  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  getAvailableActions(status: InternalReturnStatus, _document: InternalReturn): TransactionAction[] {
    const actions: TransactionAction[] = [];

    switch (status) {
      case InternalReturnStatus.Processing:
        actions.push({
          id: 'complete',
          label: 'Complete',
          icon: 'pi pi-check',
          severity: 'success',
          requiresConfirmation: true,
          confirmationMessage: this.translate(
            'IR_Confirm_Complete',
            'Are you sure you want to complete this Internal Return? This will add items back to inventory.',
          ),
        });
        actions.push({
          id: 'cancel',
          label: 'Cancel',
          icon: 'pi pi-times',
          severity: 'danger',
          requiresConfirmation: true,
          confirmationMessage: this.translate('IR_Confirm_Cancel', 'Are you sure you want to cancel this Internal Return?'),
        });
        break;

      case InternalReturnStatus.Completed:
        // Read-only, no actions
        break;

      case InternalReturnStatus.Cancelled:
        // Read-only, no actions
        break;
    }

    return actions;
  }

  canEditQuantities(status: InternalReturnStatus): boolean {
    return status === InternalReturnStatus.Processing;
  }

  canEditLines(status: InternalReturnStatus): boolean {
    return status === InternalReturnStatus.Processing;
  }

  validateForStatusChange(document: InternalReturn, newStatus: InternalReturnStatus): { valid: boolean; errors: string[] } {
    const errors: string[] = [];

    if (!document.entries || document.entries.length === 0) {
      errors.push('Internal Return must have at least one entry');
    }

    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    document.entries?.forEach((entry: any) => {
      if (entry.quantity <= 0) {
        errors.push(`${entry.itemCode}: ${this.translate('IR_Error_Invalid_Quantity_Line', 'Invalid quantity')}`);
      }
    });

    if (newStatus === InternalReturnStatus.Completed) {
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      document.entries?.forEach((entry: any) => {
        if (!entry.storageLocations || entry.storageLocations.length === 0) {
          errors.push(
            `${entry.itemCode}: ${this.translate('IR_Error_No_Storage_Locations_Line', 'La línea debe tener ubicaciones de almacenamiento asignadas')}`,
          );
        } else {
          // eslint-disable-next-line @typescript-eslint/no-explicit-any
          const totalInLocations = entry.storageLocations.reduce((sum: number, loc: any) => sum + loc.quantity, 0);
          const diff = Math.abs(totalInLocations - entry.quantity);
          if (diff > 0.001) {
            errors.push(
              `${entry.itemCode}: ${this.translate('IR_Error_Location_Mismatch_Line', 'La cantidad de las ubicaciones de almacenamiento no coincide en la línea')}`,
            );
          }
        }
      });
    }

    return {
      valid: errors.length === 0,
      errors,
    };
  }

  getDocumentTitle(document: InternalReturn | null): string {
    if (!document) return this.translate('IR_New', 'New Internal Return');
    return document.number || this.translate('IR_Title', 'Internal Return');
  }

  getInitiatorEntityKey(dispatchRequestId: string): EntityKeyByReference {
    return {
      reference: dispatchRequestId,
      entityDefinitionCode: GlobalEntityDefinition.DispatchRequest,
    };
  }

  override supportsReportDownload(): boolean {
    return true;
  }

  override canDownloadReport(status: InternalReturnStatus): boolean {
    return status === InternalReturnStatus.Processing || status === InternalReturnStatus.Completed;
  }

  override downloadReport(documentId: string): Observable<Blob> {
    return this.internalReturnService.downloadReport(documentId);
  }
}
